import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../css/MomentViewer.css';

export default function MomentViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [moment, setMoment] = useState(null);
  const [idx, setIdx] = useState(0);
  const [viewers, setViewers] = useState([]);
  const [showViewers, setShowViewers] = useState(false);
  const [viewersSkip, setViewersSkip] = useState(0);
  const [totalViewers, setTotalViewers] = useState(0);
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [viewedSlideIndices, setViewedSlideIndices] = useState([]);
  const [hasUnviewed, setHasUnviewed] = useState(false);

  // progress handling
  const progressRef = useRef(null);
  const timerRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const mediaRef = useRef(null);
  const mediaContainerRef = useRef(null);
  const [mediaContainerWidth, setMediaContainerWidth] = useState(0);

  useEffect(() => {
    const upd = () => {
      try {
        const w = mediaContainerRef.current?.getBoundingClientRect()?.width || 0;
        setMediaContainerWidth(w);
      } catch (e) {}
    };
    upd();
    window.addEventListener('resize', upd);
    return () => window.removeEventListener('resize', upd);
  }, []);

  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('jwt');
    if (!token) return;
    fetch(`/moments/${id}`, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(m => {
        setMoment(m);
        setViewedSlideIndices(m.viewedIndices || []);
        setHasUnviewed(m.hasUnviewed);
        // start from first unviewed slide
        setIdx(m.startIndex || 0);
        // mark the first slide to be shown as viewed
        const startIdx = m.startIndex || 0;
        markSlideViewed(startIdx, token);
      });
  }, [id]);

  // mark a slide as viewed and refetch to update hasUnviewed status
  const markSlideViewed = async (slideIdx, token) => {
    try {
      const res = await fetch(`/moments/${id}/view`, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ slideIndex: slideIdx })
      });
      const data = await res.json();
      if (data && data.viewedIndices) {
        const viewed = data.viewedIndices || [];
        const total = (moment && moment.media && moment.media.length) || 0;
        setViewedSlideIndices(viewed);
        setHasUnviewed(!!data.hasUnviewed);
        // broadcast precise status
        window.dispatchEvent(new CustomEvent('moment-slide-viewed', { detail: { momentId: id } }));
        window.dispatchEvent(new CustomEvent('moment-status-changed', { detail: { momentId: id, viewedCount: viewed.length, totalSlides: total, hasUnviewed: !!data.hasUnviewed } }));
      } else {
        // fallback: request full moment
        const mres = await fetch(`/moments/${id}`, { headers: { Authorization: 'Bearer ' + token } });
        const m = await mres.json();
        setViewedSlideIndices(m.viewedIndices || []);
        setHasUnviewed(!!m.hasUnviewed);
        window.dispatchEvent(new CustomEvent('moment-slide-viewed', { detail: { momentId: id } }));
        window.dispatchEvent(new CustomEvent('moment-status-changed', { detail: { momentId: id, hasUnviewed: !!m.hasUnviewed } }));
      }
      } catch (e) {
        console.error('markSlideViewed error', e);
      }
  };

  // whenever idx changes, mark that slide as viewed
  useEffect(() => {
    if (!moment || idx === undefined) return;
    const token = localStorage.getItem('jwt');
    if (!token) return;
    markSlideViewed(idx, token);
  }, [idx]);

  useEffect(() => {
    // start auto progress for current slide
    if (!moment) return;
    clearInterval(timerRef.current);
    const media = moment.media || [];
    const current = media[idx];
    if (!current) return;
    const progressEl = progressRef.current && progressRef.current.children[idx] && progressRef.current.children[idx].firstChild;
    if (current.type === 'video') {
      const vid = mediaRef.current;
      if (!vid) return;
      vid.muted = muted;
      // control play/pause via `playing` state
      if (playing) {
        vid.play().catch(() => {});
      } else {
        try { vid.pause(); } catch(e) {}
      }
      const onTime = () => {
        if (progressEl && vid.duration) progressEl.style.width = `${(vid.currentTime / vid.duration) * 100}%`;
      };
      vid.addEventListener('timeupdate', onTime);
      vid.addEventListener('ended', () => { if (playing && idx < media.length - 1) setIdx(i => i + 1); else if (playing) navigate('/'); });
      return () => {
        vid.removeEventListener('timeupdate', onTime);
      };
    } else {
      // image: show for 4 seconds (only when playing)
      if (!playing) {
        if (progressEl) progressEl.style.width = '0%';
        return;
      }
      let start = 0;
      if (progressEl) progressEl.style.width = '0%';
      timerRef.current = setInterval(() => {
        start += 100;
        if (progressEl) progressEl.style.width = `${Math.min(100, (start / 4000) * 100)}%`;
        if (start >= 4000) {
          clearInterval(timerRef.current);
          if (idx < media.length - 1) setIdx(i => i + 1);
          else navigate('/');
        }
      }, 100);
    }
    return () => clearInterval(timerRef.current);
  }, [moment, idx, playing, navigate]);

  // Reset viewers pagination when slide changes
  useEffect(() => {
    setViewersSkip(0);
  }, [idx]);

  const fetchViewers = (skip = 0) => {
    if (!id) return;
    const token = localStorage.getItem('jwt');
    // Pass current slide index to get viewers for this specific slide
    fetch(`/moments/${id}/viewers?slideIndex=${idx}&skip=${skip}&limit=20`, {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(r => r.json())
      .then(data => {
        if (skip === 0) {
          setViewers(data.viewers || []);
        } else {
          setViewers(prev => [...prev, ...(data.viewers || [])]);
        }
        setTotalViewers(data.total || 0);
        setShowViewers(true);
      });
  };

  const handleLoadMoreViewers = () => {
    const newSkip = viewersSkip + 20;
    setViewersSkip(newSkip);
    fetchViewers(newSkip);
  };

  if (!moment) return <div className="moment-viewer">Loading...</div>;

  const media = moment.media;
  const current = media[idx];

  const handleNext = () => {
    if (idx < media.length - 1) {
      const nextIdx = idx + 1;
      setIdx(nextIdx);
    } else {
      navigate('/');
    }
  };

  const handlePrev = () => {
    if (idx > 0) setIdx(i => i - 1);
  };

  const toggleMute = () => setMuted(m => !m);

  const handleMediaClick = (e) => {
    // tapping right half advances, left half goes back
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x > rect.width / 2) handleNext(); else handlePrev();
  };

  // press-and-hold to pause on media only (not on buttons)
  const handlePressStart = (e) => {
    if (e.currentTarget !== e.target) return; // only on media div itself, not children
    setPlaying(false);
  };
  const handlePressEnd = (e) => {
    if (e.currentTarget !== e.target) return;
    setPlaying(true);
  };

  return (
    <div className="moment-viewer-fullscreen">
      <div className="mv-progress-row" ref={progressRef}>
        {media.map((m, i) => (
          <div key={i} className="mv-progress"><div className="bar" style={{ width: i < idx ? '100%' : '0%' }} /></div>
        ))}
      </div>

      <div className="mv-top">
        <div className="mv-owner">
          <img src={moment.owner.Photo || '/logo192.png'} alt="" />
          <div>{moment.owner.name}</div>
        </div>
        <div className="mv-controls">
          <button className="mv-btn" onClick={() => navigate(-1)}>Close</button>
          {user && moment.owner._id === user._id && <button className="mv-btn" onClick={() => fetchViewers(0)}>Viewers</button>}
          {user && moment.owner._id === user._id && (
            <button className="mv-btn" onClick={async () => { if (window.confirm('Delete this moment?')) { await fetch(`/moments/${moment._id}`, { method: 'DELETE', headers: { Authorization: 'Bearer ' + localStorage.getItem('jwt') } }); navigate('/'); }}}>Delete Moment</button>
          )}
        </div>
      </div>

      <div className="mv-media" onClick={handleMediaClick} onMouseDown={handlePressStart} onMouseUp={handlePressEnd} onMouseLeave={handlePressEnd} onTouchStart={handlePressStart} onTouchEnd={handlePressEnd}>
        <div style={{ position: 'relative' }} ref={mediaContainerRef}>
          {current.type === 'video' ? (
            <video
              ref={mediaRef}
              src={current.url}
              controls
              autoPlay
              muted={muted}
            />
          ) : (
            <img src={current.url} alt="" />
          )}
          <div className="mv-text-overlay">
            {(current.overlays || []).map((t, i) => {
              const left = (typeof t.xPct === 'number') ? `${t.xPct * 100}%` : (t.x ? `${t.x}px` : '0px');
              const top = (typeof t.yPct === 'number') ? `${t.yPct * 100}%` : (t.y ? `${t.y}px` : '0px');
              const fontPx = (typeof t.sizePct === 'number' && mediaContainerWidth) ? Math.max(10, Math.round(t.sizePct * mediaContainerWidth)) : (t.size || 18);
              return (
                <div key={i} className="mv-text-item" style={{ left, top, fontSize: fontPx, color: t.color || '#fff' }}>{t.text}</div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mv-action-row">
        <div>
          <button className="mv-btn" onClick={handlePrev}>Prev</button>
          <button className="mv-btn" onClick={handleNext}>Next</button>
          <button className="mv-btn" onClick={() => setPlaying(p => !p)}>{playing ? 'Pause' : 'Resume'}</button>
        </div>
        <div>
          {current.type === 'video' && <button className="mv-btn" onClick={toggleMute}>{muted ? 'Unmute' : 'Mute'}</button>}
        </div>
      </div>

      {showViewers && (
        <div className="viewers-list">
          <div className="viewers-header">
            <h4>Viewed by ({totalViewers})</h4>
            <button className="close-viewers" onClick={() => setShowViewers(false)}>×</button>
          </div>
          <div className="viewers-scroll">
            {viewers.map(u => (
              <div key={u._id} className="viewer-item" onClick={() => navigate(`/profile/${u._id}`)}>
                <img src={u.Photo || '/logo192.png'} alt="" />
                <div className="viewer-info">
                  <div className="viewer-name">{u.name}</div>
                  <div className="viewer-handle">@{u.userName}</div>
                </div>
              </div>
            ))}
          </div>
          {viewers.length < totalViewers && (
            <button className="load-more-viewers" onClick={handleLoadMoreViewers}>
              Load More Viewers
            </button>
          )}
        </div>
      )}
    </div>
  );
}

